import type { TaskWorkloadPreview } from '../api/tasks';

export type WorkloadPreviewState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; data: TaskWorkloadPreview }
  | { status: 'error'; error: string };

export type WorkloadPreviewMap = Record<string, WorkloadPreviewState>;

const ACTIVE_WORKLOAD_STATUSES = new Set(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'BLOCKED']);

export function formatHours(value: number | string | null | undefined): string {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return '0h';
  const rounded = Math.round(parsed * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}h`;
}

export function toLocalDateInputValue(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function resolveTaskCapacityDate({
  startDate,
  dueDate,
  today = new Date(),
}: {
  startDate?: string | null;
  dueDate?: string | null;
  today?: Date;
}): string {
  if (startDate) return startDate.slice(0, 10);
  if (dueDate) return dueDate.slice(0, 10);
  return toLocalDateInputValue(today);
}

export function parseTaskHours(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function combineHoursAndMinutes(
  hoursValue: unknown,
  minutesValue: unknown,
): number | undefined {
  const hoursEmpty = hoursValue === null || hoursValue === undefined || hoursValue === '';
  const minutesEmpty = minutesValue === null || minutesValue === undefined || minutesValue === '';
  if (hoursEmpty && minutesEmpty) return undefined;

  const hours = hoursEmpty ? 0 : Number(hoursValue);
  const minutes = minutesEmpty ? 0 : Number(minutesValue);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return undefined;
  return Math.round((hours + minutes / 60) * 10000) / 10000;
}

export function splitHoursAndMinutes(value: unknown): { hours: string; minutes: string } {
  const parsed = parseTaskHours(value);
  if (parsed === undefined) return { hours: '', minutes: '' };

  const totalMinutes = Math.round(parsed * 60);
  const hours = Math.trunc(totalMinutes / 60);
  const minutes = Math.abs(totalMinutes % 60);
  return {
    hours: String(hours),
    minutes: minutes ? String(minutes) : '',
  };
}

export function hoursToEstimatedMinutes(value: unknown): number | undefined {
  const parsed = parseTaskHours(value);
  return parsed === undefined ? undefined : Math.round(parsed * 60);
}

export function estimatedMinutesToHours(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed / 60 : undefined;
}

export function resolveTaskEffectiveHours(
  estimatedHours: unknown,
  actualHours?: unknown,
): number | undefined {
  const actual = parseTaskHours(actualHours);
  if (actual !== undefined && actual > 0) return actual;
  return parseTaskHours(estimatedHours);
}

export function taskStatusConsumesCapacity(status?: string | null): boolean {
  return ACTIVE_WORKLOAD_STATUSES.has(String(status || 'TODO'));
}

function firstViolation(detail: any): any | null {
  const violations = detail?.details?.violations;
  return Array.isArray(violations) && violations.length > 0 ? violations[0] : null;
}

export function formatTaskApiError(error: any, fallback = 'Failed to save task'): string {
  const data = error?.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;

  if (detail && typeof detail === 'object') {
    if (detail.code === 'EMPLOYEE_CAPACITY_EXCEEDED') {
      const violation = firstViolation(detail);
      if (violation) {
        return `Cannot assign ${formatHours(violation.requested_hours)}. Only ${formatHours(violation.remaining_hours)} is available.`;
      }
    }
    return detail.message || fallback;
  }

  return error?.message || fallback;
}

export function getTaskCapacityBlockers({
  employeeIds,
  previews,
  requestedHours,
  getEmployeeName,
}: {
  employeeIds: string[];
  previews: WorkloadPreviewMap;
  requestedHours: number | undefined;
  getEmployeeName: (employeeId: string) => string;
}): string[] {
  if (employeeIds.length === 0) return [];

  // No estimate given: nothing has been requested, so nothing can exceed
  // anyone's remaining capacity and there is nothing to block on. This used to
  // return a "hours are required" blocker, which disabled the Plan / Assign
  // button outright — a demand for a number people did not have yet, enforced
  // before the work could even be written down. The backend counts an
  // unestimated item as zero hours (see effective_task_hours), so returning no
  // blockers here is the same rule, not a client-side loophole.
  //
  // Returning early also means we do not wait on the workload previews: a
  // zero-hour request cannot exceed capacity, so there is nothing their answer
  // could change.
  if (requestedHours === undefined) return [];

  if (requestedHours < 0) {
    return ['Estimated hours cannot be negative.'];
  }

  const blockers: string[] = [];
  for (const employeeId of employeeIds) {
    const state = previews[employeeId];
    if (!state || state.status === 'idle' || state.status === 'loading') {
      blockers.push('Checking employee workload...');
      continue;
    }
    if (state.status === 'error') {
      blockers.push(state.error);
      continue;
    }
    if (requestedHours > Number(state.data.remaining_hours || 0)) {
      blockers.push(
        `Cannot assign ${formatHours(requestedHours)} to ${getEmployeeName(employeeId)}. Only ${formatHours(state.data.remaining_hours)} is available.`,
      );
    }
  }

  return Array.from(new Set(blockers));
}

export function isNearCapacity(preview: TaskWorkloadPreview): boolean {
  const officeHours = Number(preview.office_hours || 0);
  const remainingHours = Number(preview.remaining_hours || 0);
  if (officeHours <= 0) return false;
  return remainingHours > 0 && remainingHours <= Math.max(1, officeHours * 0.25);
}
